"use client";

import React from "react";
import { ArrowRight, CircleCheck, FileText, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
const heroCopy = {
  categoryBadge: {
    en: "Technical Resources",
    zh: "技术资源",
    es: "Recursos técnicos",
    de: "Technische Ressourcen",
    fr: "Ressources techniques",
    pt: "Recursos técnicos"
  },
  standardBadge: {
    en: "ISO 9044 / ASTM E2016",
    zh: "ISO 9044 / ASTM E2016",
    es: "ISO 9044 / ASTM E2016",
    de: "ISO 9044 / ASTM E2016",
    fr: "ISO 9044 / ASTM E2016",
    pt: "ISO 9044 / ASTM E2016"
  },
  h1: {
    en: "Industrial Wire Mesh Product Catalog",
    zh: "工业金属丝网产品目录",
    es: "Catálogo de productos de malla metálica industrial",
    de: "Industrie-Drahtgewebe Produktkatalog",
    fr: "Catalogue de toiles métalliques industrielles",
    pt: "Catálogo de produtos de tela metálica industrial"
  },
  lead: {
    en: "Technical specifications, aperture ranges, and alloy availability across four core manufacturing lines. Built for process engineers, filter fabricators, and OEM procurement teams.",
    zh: "涵盖四大核心制造品类的网孔规格、编织结构与合金材质范围。专为工艺工程师、过滤器制造商及 OEM 采购团队打造。",
    es: "Especificaciones técnicas, rangos de apertura y aleaciones en cuatro líneas de fabricación principales. Para ingenieros de procesos, fabricantes de filtros y compras OEM.",
    de: "Technische Spezifikationen, Maschenweiten und Legierungsübersichten für vier Kernfertigungslinien. Für Verfahrensingenieure, Filterbauer und OEM-Einkaufsteams.",
    fr: "Spécifications techniques, plages d'ouverture et alliages pour quatre lignes de fabrication principales. Conçu pour les ingénieurs procédés, fabricants de filtres et acheteurs OEM.",
    pt: "Especificações técnicas, faixas de abertura e ligas em quatro linhas de fabricação principais. Desenvolvido para engenheiros de processo, fabricantes de filtros e compras OEM."
  },
  relatedBtn: {
    en: "Datasheets & Guides",
    zh: "技术规格书与指南",
    es: "Fichas técnicas y guías",
    de: "Datenblätter & Leitfäden",
    fr: "Fiches techniques et guides",
    pt: "Folhas técnicas e guias"
  },
  imageAlt: {
    en: "MutualFilter industrial wire mesh and filtration media technical catalog overview",
    zh: "MutualFilter 工业金属丝网与过滤介质技术目录概览",
    es: "Descripción general del catálogo técnico de mallas de alambre y medios de filtración MutualFilter",
    de: "Übersicht des technischen Katalogs für Industrie-Drahtgewebe und Filtermedien von MutualFilter",
    fr: "Vue d'ensemble du catalogue technique de toiles métalliques et médias filtrants MutualFilter",
    pt: "Visão geral do catálogo técnico de telas de arame e meios filtrantes MutualFilter"
  },
  imageOverlay: {
    en: "Four Product Categories",
    zh: "四大核心产品系列",
    es: "Cuatro categorías de productos",
    de: "Vier Produktkategorien",
    fr: "Quatre catégories de produits",
    pt: "Quatro categorias de produtos"
  },
  cardNote: {
    en: "Standard specifications and OEM custom fabrication",
    zh: "标准制造规格与 OEM 定制加工服务",
    es: "Especificaciones estándar y fabricación personalizada OEM",
    de: "Standardspezifikationen und OEM-Sonderanfertigung",
    fr: "Spécifications standard et fabrication sur mesure OEM",
    pt: "Especificações padrão e fabricação personalizada OEM"
  },
  cardLink: {
    en: "Downloads",
    zh: "技术资料下载",
    es: "Descargas",
    de: "Downloads",
    fr: "Téléchargements",
    pt: "Downloads"
  }
};
const specStripData: Array<{
  label: L6;
  value: string;
}> = [{
  label: {
    en: "Woven Wire Mesh",
    zh: "编织丝网",
    es: "Malla tejida",
    de: "Drahtgewebe",
    fr: "Toile tissée",
    pt: "Malha tecida"
  },
  value: "1–635 Mesh (1 µm – 25 mm)"
}, {
  label: {
    en: "Sintered Wire Mesh",
    zh: "烧结丝网",
    es: "Malla sinterizada",
    de: "Sinterdrahtgewebe",
    fr: "Toile frittée",
    pt: "Malha sinterizada"
  },
  value: "1–200 µm"
}, {
  label: {
    en: "Wedge Wire Screens",
    zh: "楔形丝筛网",
    es: "Tamices de cuña",
    de: "Spaltsiebe",
    fr: "Crépines à fil profilé",
    pt: "Telas de cunha"
  },
  value: "0.02–5.0 mm Slots"
}, {
  label: {
    en: "Filter Components",
    zh: "过滤组件",
    es: "Componentes de filtración",
    de: "Filterkomponenten",
    fr: "Composants de filtration",
    pt: "Componentes filtrantes"
  },
  value: "Tubes, Discs, Packs & Baskets"
}];
export default function CatalogHero() {
  const {
    lang
  } = useLanguage();
  return <section id="cataloghero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 Columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Badges Row - EXACTLY two outline badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{pick(lang, heroCopy.categoryBadge)}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>{pick(lang, heroCopy.standardBadge)}</span>
              </Badge>
            </div>

            {/* Single Page Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {pick(lang, heroCopy.h1)}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {pick(lang, heroCopy.lead)}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="font-semibold transition-colors">
                <Link to="/DatasheetsPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{pick(lang, heroCopy.relatedBtn)}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Hero Card (5 Columns on lg) */}
          <Reveal delay={60} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              <div className="mf-card-media mf-ratio-hero w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-product-photograph-showing-an-asso-eb0545.png" alt={pick(lang, heroCopy.imageAlt)} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono text-foreground flex items-center gap-1.5">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{pick(lang, heroCopy.imageOverlay)}</span>
                </div>
              </div>

              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{pick(lang, heroCopy.cardNote)}</span>
                  </div>
                  <Link to="/DownloadsPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{pick(lang, heroCopy.cardLink)}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {specStripData.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{pick(lang, spec.label)}</span>
              <span className="mf-spec-strip__value font-mono">{spec.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}